import { curatedCoworldName } from "@softmax/web-features/coworlds";
import type { GamePageContext } from "../game-types";
import type { GameIndexPage } from "../game-index-data";
import { gameHomeHead } from "./game-home-head";
import { sugarscapeHead } from "../sugarscape/metadata";

export function gameIndexHead(page: GameIndexPage) {
  if (page.game.viewer) return privateGameHead;

  switch (page.kind) {
    case "desk":
      return {
        meta: [
          {
            title: `${page.spec.title} · ${curatedCoworldName(page.game.coworldName) ?? page.game.coworldName} · Softmax`,
          },
          { name: "description", content: page.spec.lede },
        ],
      };
    case "sugarscape":
      return sugarscapeHead(page.game.address);
    case "home":
      return page.data.content.viewer
        ? privateGameHead
        : gameHomeHead(page.data.content);
    case "theater":
      return page.data.viewer ? privateGameHead : theaterHead(page.data);
    default:
      return fallbackGameHead(page.game);
  }
}

function fallbackGameHead(game: GamePageContext) {
  if (game.viewer) return privateGameHead;

  const name = game.coworldName
    ? (curatedCoworldName(game.coworldName) ?? game.coworldName)
    : game.address.slug;
  return {
    meta: [
      { title: `${name} · Softmax` },
      { name: "robots", content: "noindex, nofollow" },
    ],
  };
}

const privateGameHead = {
  meta: [
    { title: "Game · Softmax" },
    { name: "robots", content: "noindex, nofollow" },
  ],
};

function theaterHead(data: {
  readonly title: string;
  readonly description: string;
  readonly socialDescription: string;
  readonly canonical: string;
  readonly markdown: string | null;
  readonly indexable: boolean;
}) {
  const meta = [
    { title: data.title },
    { name: "description", content: data.description },
    { property: "og:title", content: data.title },
    { property: "og:description", content: data.socialDescription },
    { property: "og:type", content: "website" },
    { property: "og:url", content: data.canonical },
    { name: "twitter:card", content: "summary_large_image" },
    { name: "twitter:title", content: data.title },
    { name: "twitter:description", content: data.socialDescription },
    {
      name: "robots",
      content: data.indexable ? "index, follow" : "noindex, nofollow",
    },
  ];
  const links = [{ rel: "canonical", href: data.canonical }];
  return {
    meta,
    links: data.markdown
      ? [
          ...links,
          { rel: "alternate", type: "text/markdown", href: data.markdown },
        ]
      : links,
  };
}
